import { sendAnalyticsEvent } from '@/shared/api/api';
import { logEventInDevMode } from '@/shared/helpers/log-event-in-dev-mode';
import { type UseWsReturnType } from '@/shared/hooks/use-ws';
import { MessageAction, MessageType } from '@/shared/hooks/use-ws/types';
import { useAppStateRaw } from '@/shared/store/app-store/app-store';

import { type PlatformEvent, PROTOCOL } from './types';

export interface EventEnrichment {
  name: string;
  field: string;
  value: string;
}

interface EventPredefinedValues {
  user_properties: Record<string, any>;
  event_properties: Record<string, any>;
}

export interface AnalyticsOptions {
  predefinedValues: EventPredefinedValues;
  enrichEventData: EventEnrichment; // for now we decorate only 1 event during one Market
  ws: UseWsReturnType;
}

export class AnalyticsService {
  private predefinedValues: EventPredefinedValues = {} as EventPredefinedValues;
  private enrichEventData: EventEnrichment = {} as EventEnrichment;
  private ws: AnalyticsOptions['ws'];

  constructor(options: AnalyticsOptions) {
    this.predefinedValues = options.predefinedValues;
    this.enrichEventData = options.enrichEventData;
    this.ws = options.ws;
  }

  public send(event: PlatformEvent, protocol: PROTOCOL = PROTOCOL.WEBSOCKET): void {
    if (!event) {
      return;
    }

    const enriched = this.enrichEvent(event);

    if (protocol === PROTOCOL.WEBSOCKET) {
      this.sendViaWebSocket(enriched);
    } else {
      this.sendViaHttp(enriched);
    }
  }

  private sendViaWebSocket(event: PlatformEvent): void {
    logEventInDevMode(event);

    if (import.meta.env.DEV) return;

    this.ws.sendMessage(MessageType.ANALYTICS, MessageAction.EXECUTE, event);
  }

  private sendViaHttp(event: PlatformEvent): void {
    sendAnalyticsEvent(event);
  }

  private enrichEvent(event: PlatformEvent): PlatformEvent {
    const enrichRecord: Record<string, any> = {};

    const evtName = event.event_properties.name;
    if (this.enrichEventData?.name === evtName) {
      const field = this.enrichEventData.field;
      const value = this.enrichEventData.value;
      enrichRecord[field] = value;
    }

    const decoratedEvent = {
      ...event,
      user_properties: {
        session_id: useAppStateRaw.getState().marketSessionId,
        ...this.predefinedValues.user_properties, // have to be first line!
        ...event.user_properties,
      },
      event_properties: {
        ...this.predefinedValues.event_properties, // have to be first line!
        ...event.event_properties,
        ...enrichRecord,
      },
    };

    return decoratedEvent;
  }
}
